"use client";

import {
  type CSSProperties,
  Fragment,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type ReactNode,
  useEffect,
  useState,
  type FC,
  type Ref,
} from "react";
import { isString, mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { Stack } from "../Stack";
import { Flex } from "../Flex";
import { Text } from "../Text";
import type { ClassNameProp } from "../../props";
import { Tooltip } from "../Tooltip";
import { useUuid } from "../../hooks/useUuid";

import {
  tabsNavigationStyle,
  containerStyle,
  navigationStyle,
  tabContentStyle,
  tabItemStyle,
  tabStyle,
} from "./TabsNavigation.css";
import { focusVisibleOffset } from "../../theme/css/focusVisible.css";

export type TabsNavigationProps = RecipeVariants<typeof containerStyle> &
  ClassNameProp &
  Pick<HTMLAttributes<HTMLDivElement>, "style"> & {
    tabs: Tab[];
    currentID?: string;
    children?: ReactNode;
    navigationClassName?: string;
    contentClassName?: string;
    className?: string;
    indented?: boolean;
    overflowHidden?: boolean;
    onChange?: (id: string) => void;
    tabListStyle?: CSSProperties;
    /**
     * When true, all tab panels are always rendered in the DOM even when inactive.
     * Useful for preserving form state or avoiding re-mounting expensive components.
     */
    forceMount?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

export interface Tab {
  id: string;
  label: ReactNode;
  strechBefore?: boolean;
  content: ReactNode;
  disabled?: boolean;
  tooltip?: string;
}

/**
 * TabsNavigation component is used to display a list of tabs with content.
 * @todo Rename, rethink
 */
export const TabsNavigation: FC<TabsNavigationProps> = ({
  tabs,
  currentID,
  navigationClassName,
  contentClassName,
  className,
  variant = "default",
  indented = false,
  color = "default",
  style,
  tabListStyle,
  overflowHidden,
  children,
  onChange,
  forceMount = false,
  ref,
}) => {
  const [activeTab, setActiveTab] = useState<string>(
    currentID ?? tabs?.[0]?.id ?? "",
  );

  const uuid = useUuid();

  useEffect(() => {
    if (isString(currentID)) {
      setActiveTab(currentID);
    }
  }, [currentID]);

  useEffect(() => {
    onChange?.(activeTab);
  }, [activeTab]);

  const handleKeyDown: KeyboardEventHandler<HTMLUListElement> = (e) => {
    let newIndex: number;
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);

    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        if (newIndex >= 0 && newIndex < tabs.length) {
          setActiveTab(tabs[newIndex].id);
        }
        break;
      case "ArrowRight":
        e.preventDefault();
        newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        if (newIndex >= 0 && newIndex < tabs.length) {
          setActiveTab(tabs[newIndex].id);
        }
        break;
      case "Home":
        e.preventDefault();
        if (tabs.length > 0) {
          setActiveTab(tabs[0].id);
        }
        break;
      case "End":
        e.preventDefault();
        if (tabs.length > 0) {
          setActiveTab(tabs[tabs.length - 1].id);
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (currentIndex >= 0 && currentIndex < tabs.length) {
          setActiveTab(tabs[currentIndex].id);
        }
        break;
    }
  };

  return (
    <Flex
      ref={ref}
      className={mergeClasses(
        tabsNavigationStyle,
        containerStyle({ variant, color }),
        className,
      )}
      grow
      direction="column"
      style={style}
    >
      <ul
        aria-label="Tabs"
        className={mergeClasses(
          navigationStyle({ variant, indented }),
          navigationClassName,
          focusVisibleOffset,
        )}
        onKeyDown={handleKeyDown}
        style={tabListStyle}
      >
        {tabs?.map((tab) => (
          <Fragment key={tab.id}>
            {tab.strechBefore && <div style={{ flexGrow: 1 }} />}
            <div>
              <Tooltip value={tab.tooltip} placement="bottom">
                <Text
                  as="button"
                  type="button"
                  role="tab"
                  id={`tab-${tab.id}-${uuid}`}
                  aria-selected={tab.id === activeTab}
                  aria-controls={`tabpanel-${tab.id}-${uuid}`}
                  aria-disabled={tab.disabled}
                  {...(tab.id === activeTab ? { "data-active": "true" } : {})}
                  onClick={() => setActiveTab(tab.id)}
                  className={mergeClasses(
                    tabItemStyle({
                      variant,
                      color,
                      disabled: tab.disabled,
                    }),
                  )}
                  disabled={tab.disabled}
                  ellipsis
                >
                  {tab.label}
                </Text>
              </Tooltip>
            </div>
          </Fragment>
        ))}
        {children}
      </ul>

      <Stack
        data-stack="tabs-navigation"
        className={mergeClasses(
          contentClassName,
          tabContentStyle({ variant, indented }),
        )}
        overflowHidden={overflowHidden}
      >
        {tabs?.map((tab) => {
          const isActive = tab.id === activeTab;

          if (!forceMount && !isActive) {
            return null;
          }

          return (
            <div
              key={tab.id}
              role="tabpanel"
              id={`tabpanel-${tab.id}-${uuid}`}
              aria-labelledby={`tab-${tab.id}-${uuid}`}
              aria-hidden={!isActive}
              className={tabStyle}
              style={!isActive ? { display: "none" } : undefined}
            >
              {tab.content}
            </div>
          );
        })}
      </Stack>
    </Flex>
  );
};
